import React, { Component } from 'react';
import StateInfo from 'Components/state-info';
import IdleScreen from 'Components/idle-screen';
import ErrorScreen from 'Components/error-screen';
import PlayerUI from 'Components/player-ui';
import playerStates from 'Misc/player-states';
import errorTypes from 'Misc/error-types';
import serializeConaxCustomDataHeader from 'Misc/conax';
import {log, logType} from 'Misc/log'; 
import {
    apiGetVideoPlaybackInfo, 
    apiGetChannelPlaybackInfo, 
    apiGetVideoAttributes, 
    apiGetChannelAttributes, 
    apiReportPlayback,
    apiGetConfig,
  } from './api';
import * as AdobeTracking from 'Misc/adobe-tracking';
import loadScript from 'Misc/utils';
import './App.scss';

class App extends Component {
  constructor(props) {
    super(props);
    this.cast = window.cast;
    this.castContext = window.castContext;
    this.castPlayer = this.castContext.getPlayerManager();

    this.castPlayerData = new this.cast.framework.ui.PlayerData();
    this.castPlayerDataBinder = new this.cast.framework.ui.PlayerDataBinder(this.castPlayerData);

    this.errorTimer = null;
    this.errorTime = 5000;

    this.state = {
      config: null,
      ui: this.cast.framework.ui.State.LAUNCHING,
      error: false,
      playbackReportedTime: 0,
      lastError: '',
      requestId: '',
      videoResolution: '',
      sonic: {
        host: '',
        token: '',
      },
      player: {
        state: '',
      },
      currentTime: 0,
      duration: 0,
      liveRange: 0,
      media: null,
      mediaAttributes: null
    }

    this.settings = {
      "APP_NAME": APP_NAME,
      "DEBUG": DEBUG === "true",
      "DISABLE_ADOBE": DISABLE_ADOBE === "true",
      "ULTRA": this.castContext.canDisplayType("video/mp4", "hev1.1.2.L150", 3840, 2160) //not 100% solution because TV has to support 4k too
    };

    log(logType.INFO, this.settings);

    this.castContextOnReadyEvent = this.castContextOnReadyEvent.bind(this);
    this.castPlayerOnLoadEvent = this.castPlayerOnLoadEvent.bind(this);
    this.castOnPlayerAnyEvent = this.castOnPlayerAnyEvent.bind(this);
    this.castOnPlayerPlayingEvent = this.castOnPlayerPlayingEvent.bind(this);
    this.castOnPlayerPauseEvent = this.castOnPlayerPauseEvent.bind(this);
    this.castOnPlayerBufferingEvent = this.castOnPlayerBufferingEvent.bind(this);
    this.castOnPlayerSeekingEvent = this.castOnPlayerSeekingEvent.bind(this);
    this.castOnPlayerSeekedEvent = this.castOnPlayerSeekedEvent.bind(this);
    this.castOnPlayerTimeUpdateEvent = this.castOnPlayerTimeUpdateEvent.bind(this);
    this.showError = this.showError.bind(this);
    this.castOnPlayerErrorEvent = this.castOnPlayerErrorEvent.bind(this);
    this.castOnPlayerMediaStatusEvent = this.castOnPlayerMediaStatusEvent.bind(this);
    this.castOnPlayerMediaFinishedEvent = this.castOnPlayerMediaFinishedEvent.bind(this);
    this.castPlayerStateChangedEvent = this.castPlayerStateChangedEvent.bind(this);
    this.castOnPlayerLoadCompleteEvent = this.castOnPlayerLoadCompleteEvent.bind(this);

    this.configPromise = null;
  }

  componentDidMount() {    
    // setup receiver:
    this.castContext.addEventListener(cast.framework.system.EventType.READY, this.castContextOnReadyEvent);    
    this.castPlayer.setMessageInterceptor(this.cast.framework.messages.MessageType.LOAD, this.castPlayerOnLoadEvent);
    this.castPlayer.addEventListener(this.cast.framework.events.EventType.ALL, this.castOnPlayerAnyEvent);
    this.castPlayer.addEventListener(this.cast.framework.events.EventType.PLAYING, this.castOnPlayerPlayingEvent);
    this.castPlayer.addEventListener(this.cast.framework.events.EventType.PAUSE, this.castOnPlayerPauseEvent);
    this.castPlayer.addEventListener(this.cast.framework.events.EventType.BUFFERING, this.castOnPlayerBufferingEvent);
    this.castPlayer.addEventListener(this.cast.framework.events.EventType.SEEKING, this.castOnPlayerSeekingEvent);
    this.castPlayer.addEventListener(this.cast.framework.events.EventType.SEEKED, this.castOnPlayerSeekedEvent);
    this.castPlayer.addEventListener(this.cast.framework.events.EventType.TIME_UPDATE, this.castOnPlayerTimeUpdateEvent);
    this.castPlayer.addEventListener(this.cast.framework.events.EventType.ERROR, this.castOnPlayerErrorEvent);
    this.castPlayer.addEventListener(this.cast.framework.events.EventType.MEDIA_FINISHED, this.castOnPlayerMediaFinishedEvent);
    this.castPlayer.addEventListener(this.cast.framework.events.EventType.PLAYER_LOAD_COMPLETE, this.castOnPlayerLoadCompleteEvent);
    
    if (this.settings.DEBUG) {
      this.castPlayer.addEventListener(this.cast.framework.events.EventType.MEDIA_STATUS, this.castOnPlayerMediaStatusEvent);
    }

    this.castPlayerDataBinder.addEventListener(cast.framework.ui.PlayerDataEventType.STATE_CHANGED, this.castPlayerStateChangedEvent);
  
    this.castPlayer.setMediaPlaybackInfoHandler((loadRequest, playbackConfig) => {
      log(logType.LOG, 'SETTING DRM LICENSE:');
      log(logType.LOG, playbackConfig);
      const request = loadRequest.media.customData;
      if (request && request.protection) {
        //Assume NONE PROTECTION
        playbackConfig.protectionSystem = this.cast.framework.ContentProtection.NONE;

        //CHECK CLEARKEY
        if (request.protection.clearkeyEnabled) {
          playbackConfig.licenseUrl = request.protection.schemes.clearkey.licenseUrl;
          playbackConfig.protectionSystem = this.cast.framework.ContentProtection.CLEARKEY;
        }
        //CHECK CONAX DRM
        if (request.protection.drmEnabled) {
          playbackConfig.licenseUrl = request.protection.schemes.widevine.licenseUrl;
          playbackConfig.protectionSystem = this.cast.framework.ContentProtection.WIDEVINE;
          playbackConfig.licenseRequestHandler = (requestInfo) => {
            requestInfo.withCredentials = false;
            requestInfo.headers["PreAuthorization"] = request.protection.drmToken;
            requestInfo.headers["Conax-Custom-Data"] = serializeConaxCustomDataHeader(request.protection.drmToken, request.protection.drmDeviceId);
          };
        }
      }
      return playbackConfig;
    });
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.state.ui !== this.cast.framework.ui.State.LAUNCHING
      && prevState.ui === this.cast.framework.ui.State.LAUNCHING
    ) {
      document.getElementById('launching-screen').remove();
    }
  }

  reportPlayback(currentMediaTimeSeconds) {
    apiReportPlayback(
      this.state.media.customData.host, 
      this.state.media.customData.token, 
      this.state.playbackReportedTime > 0 ? "PUT" : "POST", 
      this.state.media.streamType,
      this.state.media.contentIdSonic,
      currentMediaTimeSeconds)
    .then(() => {
      log(logType.INFO, `Playback reported at ${currentMediaTimeSeconds * 1000}`);
    }).catch(() => {
      log(logType.WARN, "Playback report error.");
    });
  }

  castContextOnReadyEvent() {
    log(logType.INFO, 'CONTEXT IS READY');
    this.setState({
      ui: this.cast.framework.ui.State.IDLE
    });
  }

  castPlayerStateChangedEvent(state) {
    this.setState({
      ui: state.value
    });
  }

  castPlayerOnLoadEvent(event) {
    log(logType.INFO, 'MEDIA LOAD');
    log(logType.LOG, event);

    this.setState({
      player: {
        state: playerStates.buffering,
      }
    })
    clearTimeout(this.errorTimer);

    try {
      log(logType.WARN, 'AdobeTracking force trackExit on media load');
      AdobeTracking.trackExit();
    } catch(e) {
      log(logType.WARN, 'AdobeTracking forced trackExit on media load failed');
    }

    return new Promise((resolve, reject) => {
      const onSuccess = (streamInfo) => {
        if (!streamInfo.data) {
          throw streamInfo.errors && streamInfo.errors[0].code ? streamInfo.errors[0].code : "";
        }

        // fix for old android sdk
        if (event.media.streamType === "LIVE") {
          event.currentTime = undefined;
        }

        log(logType.INFO, 'DRM SCHEMES:');
        log(logType.LOG, streamInfo.data.attributes.protection.schemes);
        const mediaUrl = `${streamInfo.data.attributes.streaming.dash.url}${this.settings.ULTRA ? '':'&bw_high=3200'}`; // bw_high=3200 - tmp solution to get 720p as highest as chromecast firmware breaks on 1080p
        log(logType.INFO, mediaUrl);
        event.media.contentIdSonic = event.media.contentId;
        event.media.customData.contentId = event.media.contentId;

        event.media.contentId = mediaUrl;
        event.media.contentType = 'application/dash+xml';
        
        event.media.customData.protection = streamInfo.data.attributes.protection;

        log(logType.INFO, 'STREAM INFO TO LOAD:');
        log(logType.INFO, event.media);

        this.setState({
          error: false,
          lastError: errorTypes.null,
          reportProgressInterval: Math.floor(streamInfo.data.attributes.reportProgressInterval / 1000) || 60,
          playbackReportedTime: 0,
          requestId: event.requestId,
          media: event.media,
          videoResolution: '',
          player: {
            state: playerStates.buffering,
          },
          sonic: {
            host: (event.media.customData && event.media.customData.host) ? event.media.customData.host : '',
            token: (event.media.customData && event.media.customData.token) ? event.media.customData.token : '',
          },
          currentTime: 0,
          duration: 0
        });
      };

      const onAttributesSuccess = content => {
        log(logType.LOG, content);

        if (!content.data) {
          throw content.errors && content.errors[0].code ? content.errors[0].code : "";
        } 

        if (this.settings.DISABLE_ADOBE === false && AdobeTracking.ADBMobile && AdobeTracking.ADBMobile.media) {
          let mediaObject = null;

          const analyticsId = content.data.attributes.identifiers && content.data.attributes.identifiers.analyticsId ? 
            content.data.attributes.identifiers.analyticsId : content.data.id;

          if (event.media.streamType === "LIVE") {
            mediaObject = AdobeTracking.createMediaObject({
              name: content.data.attributes.name, 
              analyticsId,
              length: 0,
              streamType: AdobeTracking.ADBMobile.media.StreamType.LIVE
            });
          } else {
            const duration = content.data.attributes.videoDuration ? 
              Math.floor(content.data.attributes.videoDuration / 1000) : 0;

            const show = content.included ? content.included.filter(item => item.type === "show") : [];            

            mediaObject = AdobeTracking.createMediaObject({
              name: content.data.attributes.name, 
              analyticsId,
              length: duration,
              showName: show.length ? show[0].attributes.name : "",
              streamType: AdobeTracking.ADBMobile.media.StreamType.VOD,
              seasonNumber: content.data.attributes.seasonNumber,
              episodeNumber: content.data.attributes.episodeNumber,
            });
          }

          AdobeTracking.trackSessionStart(mediaObject);
        }

        this.setState({
          mediaAttributes: content.data.attributes
        }, () => {
          resolve(event);
        });
      };

      const onFailure = (error) => {
        log(logType.ERROR, "load error");
        log(logType.ERROR, error)
        let loadErrorType;

        switch (error) {
          case "access.denied.geoblocked":
            loadErrorType = errorTypes.geoblock;
            break;
          default:
            loadErrorType = errorTypes.generic;
        }
        this.setState({
          lastError: loadErrorType
        }, () => {
          log(logType.INFO, `Load error ${this.state.lastError}. Load will be rejected`);
          reject(new this.cast.framework.messages.ErrorData(this.cast.framework.messages.ErrorType.INVALID_REQUEST));
        });        
      };
      
      const contentId = event.media.contentId;
      const { host, token } = event.media.customData;

      if (event.media.streamType === "LIVE") {
        this.getConfig(host, token)
        .then(() => apiGetChannelPlaybackInfo(host, token, contentId))
        .then(data => data.json())
        .then(onSuccess)
        .then(() => apiGetChannelAttributes(host, token, contentId))
        .then(data => data.json())
        .then(onAttributesSuccess)
        .catch(onFailure);
      } else {
        this.getConfig(host, token)
        .then(() => apiGetVideoPlaybackInfo(host, token, contentId))
        .then(data => data.json())
        .then(onSuccess)
        .then(() => apiGetVideoAttributes(host, token, contentId))
        .then(data => data.json())
        .then(onAttributesSuccess)
        .catch(onFailure);
      }

    });
  }

  initAdobe(config) {
    if (this.settings.DISABLE_ADOBE === false) {
      AdobeTracking.initADBMobile(
        config,
        this.state, 
        this.settings.debug
      );
    }
  }

  initMux(config) {
    const { mux } = config;
    if (mux && mux.enabled && mux.libraryUrl) {
      loadScript(mux.libraryUrl, () => this.setState({
        muxLoaded: true,
      }));
    }
  }

  trackMux(config){
    const { mux } = config;
    if(mux && mux.enabled ){
      const context = cast.framework.CastReceiverContext.getInstance();
      const playerManager = context.getPlayerManager();
      console.log('init Mux: ', mux);
      this.setState({
        muxTracked: true,
      })

      window.initChromecastMux(playerManager, {
        debug: mux.debug,
        data : {
          env_key: mux.env_key,
          player_name: mux.player_name,
          player_init_time: Date.now(),
        }
      });

      context.start();
    }
  }

  getConfig(host, token) {
    if (!this.configPromise) {
      this.configPromise = apiGetConfig(host, token, `${this.settings.APP_NAME}-chromecast`)
      .then(data => data.json())
      .then(sonicConfig => {
        if (sonicConfig.errors && sonicConfig.errors.length) {
          throw new Error();
        }
        const { config } = sonicConfig.data.attributes;
        this.setState({
          config
        });
        this.initAdobe(config);
        this.initMux(config);
      });
    }
    return this.configPromise;
  }

  castOnPlayerAnyEvent(event) {
    log(logType.INFO, `PLAYER EVENT ${event.type}`);
    log(logType.INFO, event);
  }

  castOnPlayerPlayingEvent(event) {
    log(logType.INFO, `PLAYER EVENT ${event.type}`);
    if (this.settings.DISABLE_ADOBE === false) { 
      try {
        AdobeTracking.trackPlay();
        if (this.state.muxLoaded && !this.state.muxTracked) {
          this.trackMux(this.state.config);
        }
      } catch(e) {
        log(logType.WARN, 'AdobeTracking trackPlay failed');
      }
    }
    this.setState({
      ui: this.cast.framework.ui.State.PLAYING,
      player: {
        state: playerStates.playing
      }
    });
  }

  castOnPlayerPauseEvent(event) {
    log(logType.INFO, `PLAYER EVENT ${event.type}`);
    const currentMediaTimeSeconds = Math.floor(this.state.currentTime);
    if (currentMediaTimeSeconds) {
      this.reportPlayback(currentMediaTimeSeconds);

      if (this.settings.DISABLE_ADOBE === false) {
        try {
          AdobeTracking.trackPause();
        } catch(e) {
          log(logType.WARN, 'AdobeTracking trackPause failed');
        }
      }

      this.setState({
        playbackReportedTime: currentMediaTimeSeconds,
        player: {
          state: playerStates.pause
        }
      });
    } else {
      this.setState({
        player: {
          state: playerStates.pause
        }
      });
    }
  }

  castOnPlayerBufferingEvent(event) {
    log(logType.INFO, `PLAYER EVENT ${event.type}`);
    let newState;
    if (event.isBufferring) {
      newState = playerStates.buffering;
    } else {
      newState = playerStates[this.castPlayer.getPlayerState().toLowerCase()];
    }
    this.setState({
      player: {
        state: newState
      }
    });
  }

  castOnPlayerSeekingEvent(event) {
    log(logType.INFO, `PLAYER EVENT ${event.type}`);
    if (this.settings.DISABLE_ADOBE === false) {
      try {
        AdobeTracking.trackSeekStart();
      } catch(e) {
        log(logType.WARN, 'AdobeTracking trackSeekStart failed');
      }
    }
    this.setState({
      player: {
        state: playerStates.seeking
      }
    });
  }
  
  castOnPlayerSeekedEvent(event) {
    log(logType.INFO, `PLAYER EVENT ${event.type}`);
    let newState = playerStates[this.castPlayer.getPlayerState().toLowerCase()];

    const currentMediaTimeSeconds = Math.floor(this.state.currentTime);
    if (currentMediaTimeSeconds) {
      this.reportPlayback(currentMediaTimeSeconds);
      if (this.settings.DISABLE_ADOBE === false) {
        try {
          AdobeTracking.trackSeekComplete();
        } catch(e) {
          log(logType.WARN, 'AdobeTracking trackSeekComplete failed');
        }
      }

      this.setState({
        playbackReportedTime: currentMediaTimeSeconds,
        player: {
          state: newState
        }
      });
    } else {
      this.setState({
        player: {
          state: newState
        }
      });
    }
  }

  castOnPlayerTimeUpdateEvent(event) {
    log(logType.INFO, `PLAYER EVENT ${event.type} currentMediaTime: ${event.currentMediaTime}`);
    const currentMediaTimeSeconds = Math.floor(event.currentMediaTime);
    let duration = 0;
    if (this.state.media.streamType === 'LIVE') {
      const range = this.castPlayer.getLiveSeekableRange();
      duration = range ? range.end - range.start : 0;
    } else {
      duration = this.castPlayer.getDurationSec();
    }

    if (currentMediaTimeSeconds 
      && (
        this.state.playbackReportedTime === 0
        || Math.abs(currentMediaTimeSeconds - this.state.playbackReportedTime) >= this.state.reportProgressInterval)
      ) {
      this.reportPlayback(currentMediaTimeSeconds);

      this.setState({
        playbackReportedTime: currentMediaTimeSeconds,
        currentTime: event.currentMediaTime,
        duration
      });
    } else {
      this.setState({
        currentTime: event.currentMediaTime,
        duration
      });
    }
  }

  showError() {
    log(logType.INFO, 'ERROR - SHOW UI WITH MESSAGE');
    const errorState = {
      error: true,
    };

    if (this.state.lastError === errorTypes.null) {
      Object.assign(errorState, {
        lastError: errorTypes.generic
      });
    }

    this.setState(errorState, () => {
      log(logType.INFO, `IS SYSTEM READY? ${this.castContext.isSystemReady()}`);
      if (this.castContext.isSystemReady() && this.state.config) {
        this.errorTimer = setTimeout(() => {
          this.setState({
            error: false,
            ui: this.castPlayer.getPlayerState().toLowerCase(),
            lastError: errorTypes.null
          });
        }, this.errorTime);
      } else {
        this.errorTimer = setTimeout(() => {
          this.castContext.stop();
        }, this.errorTime);
      }
    });
  }

  castOnPlayerErrorEvent(event) {
    log(logType.INFO, `ERROR - player state is ${this.castPlayer.getPlayerState().toLowerCase()}`);
    log(logType.INFO, event);

    if (event.error
      && event.error.reason === this.cast.framework.messages.ErrorReason.INVALID_MEDIA_SESSION_ID 
      && this.castPlayer.getPlayerState().toLowerCase() === playerStates.playing
    ) {
      log(logType.INFO, 'ERROR - STOP SHOWING UI WITH MESSAGE');   
      return;
    }

    if (event.error
      && event.error.type === this.cast.framework.messages.ErrorType.LOAD_CANCELLED 
      && this.castPlayer.getPlayerState().toLowerCase() === playerStates.idle
    ) {
      log(logType.INFO, 'ERROR - STOP SHOWING UI WITH MESSAGE');   
      return;
    }

    this.showError();
  }

  castOnPlayerMediaStatusEvent(event) {
    log(logType.INFO, `PLAYER EVENT ${event.type}`);
    if (event.mediaStatus && event.mediaStatus.videoInfo) {
      this.setState({
        videoResolution: `${event.mediaStatus.videoInfo.width}x${event.mediaStatus.videoInfo.height}`,
      });
    }
  }

  castOnPlayerLoadCompleteEvent(event) {
    log(logType.INFO, `PLAYER EVENT ${event.type}`);
    log(logType.INFO, `PLAYER LOAD COMPLETE TRACKS ${JSON.stringify(event.media.tracks)}`);
    const textTracksManager = this.castPlayer.getTextTracksManager();
    const tracks = textTracksManager.getTracks();

    if (tracks.length > 0) {
      // Choose the first text track to be active by its ID
      textTracksManager.setActiveByIds([tracks[0].trackId]);
    }
  }

  castOnPlayerMediaFinishedEvent(event) {
    log(logType.INFO, `PLAYER EVENT ${event.type}`);
    const currentMediaTimeSeconds = Math.floor(event.currentMediaTime);

    if (currentMediaTimeSeconds) {
      this.reportPlayback(currentMediaTimeSeconds);
      if (this.settings.DISABLE_ADOBE === false && event.endedReason !== this.cast.framework.events.EndedReason.INTERRUPTED) {
        // do not end session when new media is playing, it causes new session to break
        // the old session is ended in onLoad handler 
        try {
          if (event.endedReason === this.cast.framework.events.EndedReason.ERROR) {
            AdobeTracking.trackExit();
          } else {
            AdobeTracking.trackEnded();
          }
        } catch(e) {
          log(logType.WARN, 'AdobeTracking trackEnded failed');
        }
      }

      this.setState({
        player: {
          state: '',
        },
        playbackReportedTime: currentMediaTimeSeconds,
      });
    }
  }

  render() {
    return (
      <div className="app">
        {(this.state.ui === this.cast.framework.ui.State.IDLE && <IdleScreen settings={this.settings} />)}
        {(this.state.ui !== this.cast.framework.ui.State.LAUNCHING
          && this.state.ui !== this.cast.framework.ui.State.IDLE 
          && <PlayerUI settings={this.settings} state={this.state} playerStates={playerStates} />)}
        {(this.state.error === true && <ErrorScreen state={this.state} settings={this.settings} />)} 
        {(DEBUG === 'true' && <StateInfo state={this.state} settings={this.settings} />)}
      </div>
    );
  }
}

export default App;



// WEBPACK FOOTER //
// ./src/App.js